import React, { useState } from "react";
import { AlertTriangle, Loader2 } from "lucide-react";
import { Modal } from "./Modal";

export interface ConfirmAction {
  title: string;
  description: string;
  confirmLabel: string;
  onConfirm: () => Promise<void> | void;
}

export interface ConfirmActionModalProps {
  action: ConfirmAction | null;
  onClose: () => void;
}

export const ConfirmActionModal: React.FC<ConfirmActionModalProps> = ({ action, onClose }) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleConfirm = async () => {
    if (!action) return;
    setIsProcessing(true);
    setError(null);
    try {
      await action.onConfirm();
      onClose();
    } catch {
      setError("The action could not be completed. Please try again.");
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Modal
      isOpen={Boolean(action)}
      onClose={() => {
        if (!isProcessing) onClose();
      }}
      title={action?.title || "Confirm action"}
      icon={<AlertTriangle className="w-5 h-5 text-rose-600" />}
      maxWidth="max-w-md"
    >
      <div className="space-y-5">
        <p className="text-sm leading-relaxed text-slate-600">{action?.description}</p>
        {error && (
          <p role="alert" className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-xs text-rose-700">
            {error}
          </p>
        )}
        <div className="flex justify-end gap-2 border-t border-slate-100 pt-4">
          <button
            type="button"
            onClick={onClose}
            disabled={isProcessing}
            className="rounded-lg border border-slate-200 px-4 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={isProcessing || !action}
            className="inline-flex items-center gap-2 rounded-lg bg-rose-600 px-4 py-2 text-xs font-bold text-white hover:bg-rose-700 disabled:opacity-50"
          >
            {isProcessing && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            {isProcessing ? "Working..." : action?.confirmLabel || "Confirm"}
          </button>
        </div>
      </div>
    </Modal>
  );
};